import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import { TSubscription } from 'src/types/TSubscription';
import {
  getAiRates,
  getFeatureTrials,
  getInvoices,
  getPlanLimits,
  getPlans,
  getSchemaAddon,
  getSubscription,
  getTopupPacks,
  getWriteStatus,
  TAiRates,
  TFeatureTrialsResponse,
  TInvoiceList,
  TPlanCatalog,
  TPlanLimits,
  TSchemaAddonSummary,
  TTopupPack,
  TWriteStatus,
} from '../functions/subscriptionApi';
import { queryNames } from '../utils/queryNames';

export function useSubscriptionQuery(queryOptions?: UseQueryOptions<TSubscription, FetchError>) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getSubscription(accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TSubscription, FetchError>([queryNames.subscription], queryFn, {
    retry: false,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
}

// Whether the account is refusing writes — see `useWritesPaused`. Keyed UNDER
// `subscription` so that every `invalidateQueries([queryNames.subscription])`
// (checkout complete, plan change, spend-limit change) refreshes it too — the
// block lifts server-side immediately and the buttons should follow without a
// reload. The short staleTime covers changes made elsewhere.
export function useWriteStatusQuery(queryOptions?: UseQueryOptions<TWriteStatus, FetchError>) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getWriteStatus(accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TWriteStatus, FetchError>([queryNames.subscription, queryNames.writeStatus], queryFn, {
    retry: false,
    staleTime: 60 * 1000,
    ...queryOptions,
  });
}

// Self-serve tier catalog + list rates. Pure catalog data (no per-account
// state), so it is cached hard — a repricing lands within the hour, and every
// consumer reads the same numbers instead of hardcoding them.
export function usePlansQuery(
  queryOptions?: UseQueryOptions<TPlanCatalog, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getPlans(accessToken)) as any;
    return (result.data ?? result) as TPlanCatalog;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TPlanCatalog, FetchError>([queryNames.plans], queryFn, {
    retry: false,
    staleTime: 60 * 60 * 1000,
    ...queryOptions,
  });
}

// Schema add-on summary (PAYG, migration 54) — usage + caps for the
// buy-schemas stepper. `enabled` lets the caller skip the fetch on plans
// that don't sell the add-on.
export function useSchemaAddonQuery(
  queryOptions?: UseQueryOptions<TSchemaAddonSummary, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getSchemaAddon(accessToken)) as any;
    return (result.data ?? result) as TSchemaAddonSummary;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TSchemaAddonSummary, FetchError>(
    [queryNames.schemaAddon], queryFn, {
      retry: false,
      staleTime: 5 * 60 * 1000,
      ...queryOptions,
    });
}

// Feature trials on this account (Phase 9): running/finished trials +
// which features can still start one. Client-synthesized plan gates (pages
// that block BEFORE any 403 arrives, e.g. Delivery Insights) read
// `available_features` to decide whether to show the trial CTA — the
// server-emitted gates carry the same answer inside the 403 payload.
export function useFeatureTrialsQuery(
  queryOptions?: UseQueryOptions<TFeatureTrialsResponse, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getFeatureTrials(accessToken)) as any;
    return (result.data ?? result) as TFeatureTrialsResponse;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TFeatureTrialsResponse, FetchError>(
    [queryNames.featureTrials], queryFn, {
      retry: false,
      staleTime: 5 * 60 * 1000,
      ...queryOptions,
    });
}

// Live-count entitlement caps (collections / shares / asset_schemas) — any
// member may read. Drives the proactive "X of Y" counters + disabled create
// buttons on the Collections, Shares and Schema Settings pages. Invalidate
// [queryNames.planLimits] after creating/deleting one of the counted things.
export function usePlanLimitsQuery(
  queryOptions?: UseQueryOptions<TPlanLimits, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getPlanLimits(accessToken)) as any;
    return (result.data ?? result) as TPlanLimits;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TPlanLimits, FetchError>([queryNames.planLimits], queryFn, {
    retry: false,
    staleTime: 60 * 1000,
    ...queryOptions,
  });
}

// Purchasable AI-credit top-up packs (owner-only endpoint). `enabled` lets the
// caller skip the fetch for non-owners / when checkout is unconfigured.
export function useTopupPacksQuery(
  queryOptions?: UseQueryOptions<TTopupPack[], FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getTopupPacks(accessToken)) as any;
    return (result.data ?? result).packs as TTopupPack[];
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TTopupPack[], FetchError>([queryNames.topupPacks], queryFn, {
    retry: false,
    staleTime: 30 * 60 * 1000,
    ...queryOptions,
  });
}

// AI credit rate card — what each AI operation costs, straight from
// `ai_credit_rates` (sysadmin-editable, so never hardcoded client-side).
export function useAiRatesQuery(
  queryOptions?: UseQueryOptions<TAiRates, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getAiRates(accessToken)) as any;
    return (result.data ?? result) as TAiRates;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TAiRates, FetchError>([queryNames.aiRates], queryFn, {
    retry: false,
    staleTime: 30 * 60 * 1000,
    ...queryOptions,
  });
}

// Recent invoices (owner-only endpoint). `enabled` lets the caller skip the
// fetch for non-owners.
//
// Returns the whole `{ invoices, has_more }` envelope rather than just the
// rows: `has_more` is what lets the section offer the Customer Portal instead
// of pretending one page is the full history. Defaulted to `false` so an older
// backend (no `has_more` in the payload) degrades to today's behaviour rather
// than rendering an undefined-driven link.
//
// `staleTime` must stay well under 10 days — `invoice_url` values are
// short-lived Stripe URLs, so these rows are only ever read live, never
// persisted.
export function useInvoicesQuery(
  queryOptions?: UseQueryOptions<TInvoiceList, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getInvoices(accessToken)) as any;
    const payload = (result.data ?? result) as Partial<TInvoiceList>;
    return {
      invoices: payload.invoices ?? [],
      has_more: payload.has_more ?? false,
    } as TInvoiceList;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<TInvoiceList, FetchError>([queryNames.invoices], queryFn, {
    retry: false,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
}
